import React, { useState, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, MessageSquare } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { logFirebaseEvent } from '@/lib/tracking';

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [openedDraft, setOpenedDraft] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams({
      subject,
      body: `${message}\n\nFrom: ${name}\nReply to: ${email}`,
    });
    window.location.href = `mailto:danielgaskins99@gmail.com?${params.toString().replace(/\+/g, '%20')}`;
    setOpenedDraft(true);
    void logFirebaseEvent('contact_email_intent', { source: 'contact_form' });
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20 py-12 md:py-24">
      <SEO 
        title="Contact Us"
        description="Get in touch with the RunOffTrack team for support, sales, or general inquiries."
        canonical="/contact"
      />

      <div className="container px-4 max-w-6xl mx-auto">
        
        <div className="grid md:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          {/* Left Column: Info */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">Get in touch</h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Tell us how many active sites you manage and how you prepare inspection reports today. Ask about pricing, the workflow, or getting started.
              </p>
            </div>

            <div className="space-y-6 pt-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 text-primary rounded-lg mt-1">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Discuss your workflow</h3>
                  <p className="text-muted-foreground">Include your current reporting process and what you need help with.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 text-primary rounded-lg mt-1">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Email Us</h3>
                  <p className="text-muted-foreground">Drop us a line at <a href="mailto:danielgaskins99@gmail.com" className="text-primary hover:underline">danielgaskins99@gmail.com</a></p>
                </div>
              </div>
              {/* <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 text-primary rounded-lg mt-1">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Headquarters</h3>
                  <p className="text-muted-foreground">123 Automation Ave, Suite 100<br/>San Francisco, CA 94105</p>
                </div>
              </div> */}
            </div>
          </div>

          {/* Right Column: Form */}
          <Card className="border-border shadow-2xl bg-card">
            <CardHeader className="p-8 pb-4">
              <CardTitle className="text-2xl">Prepare an email</CardTitle>
              <CardDescription>This opens your email app. Review the draft and send it there.</CardDescription>
            </CardHeader>
            <CardContent className="p-8 pt-2">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required maxLength={100} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@company.com" required maxLength={200} />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="How can we help?" required maxLength={150} />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I manage … active sites. Today I create inspection reports using …" className="min-h-[150px]" required maxLength={1500} />
                </div>

                <Button type="submit" className="w-full h-12 text-base">
                  <Mail className="mr-2 h-5 w-5" />
                  Open email draft
                </Button>
                {openedDraft && <p role="status" className="text-sm text-muted-foreground">Your message has not been sent by this website. Send it from your email app. If no draft opened, copy your message and email danielgaskins99@gmail.com directly.</p>}
              </form>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default ContactPage;
